import { sha256 } from "js-sha256";

import { useFeatureToggle } from "@hooks/useFeatureToggle";

import type {
  Payload,
  DataLayer,
  GTMWindowWithPush,
  GTMWindowWithDataLayer,
} from "./useGTM.types";
import * as BookingFunnel from "./types/bookingFunnel";

/**
 * @warning Remember to use the `encodeSHA256` function to encode sensitive fields that have `SHA256` in their name before pushing to the data layer.
 */
export const useGTM = () => {
  const isGTMEnabled = useFeatureToggle("gtm");

  const encodeSHA256 = (value: string): string => {
    return sha256(value);
  };

  const getDataLayer = (): DataLayer | null => {
    const gtmWindow = (window || {}) as GTMWindowWithDataLayer | undefined;

    if (isGTMEnabled && !!gtmWindow && Array.isArray(gtmWindow.dataLayer)) {
      return gtmWindow.dataLayer;
    }

    return null;
  };

  function push(payload: Payload): void;
  function push(
    payload:
      | BookingFunnel.CleaningInformationDL
      | BookingFunnel.CheckoutDL
      | BookingFunnel.PaymentDL
      | BookingFunnel.PurchaseDL,
    eventName: typeof BookingFunnel.BOOKING_FUNNEL_VIEW,
  ): void;
  function push(
    payload:
      | Payload
      | BookingFunnel.CleaningInformationDL
      | BookingFunnel.CheckoutDL
      | BookingFunnel.PaymentDL
      | BookingFunnel.PurchaseDL,
    eventName?: typeof BookingFunnel.BOOKING_FUNNEL_VIEW,
  ): void {
    const gtmWindow = (window || {}) as GTMWindowWithPush | undefined;

    if (!isGTMEnabled || typeof gtmWindow?.dataLayer?.push !== "function") {
      return;
    }

    if (typeof eventName === "string") {
      gtmWindow.dataLayer.push({
        event: eventName,
        ...payload,
      });

      return;
    }

    gtmWindow.dataLayer.push(payload as Payload);
  }

  return {
    isGTMEnabled,
    push,
    getDataLayer,
    encodeSHA256,
  };
};
